import { lookFor } from '@monorepo/shared/utils/lookFor';
import { format } from 'date-fns';
import {
  DocumentFileResponse,
  DocumentFolderWithFileCountResponse,
} from 'mapistry-shared';
import {
  DOCUMENTS_TABLE_ROW_KIND,
  EXPANDED_FOLDER_PLACEHOLDER,
} from './consts';
import {
  DocumentsFileRow,
  DocumentsFolderRow,
  DocumentsPlaceholderRow,
  DocumentsTableRow,
} from './types';

export function composeDocumentsFolderRow(
  folder: DocumentFolderWithFileCountResponse,
): DocumentsFolderRow {
  return {
    kind: DOCUMENTS_TABLE_ROW_KIND.FOLDER,
    id: folder.id,
    parentFolderId: null,
    folder,
  };
}

export function composeDocumentsFileRow(
  file: DocumentFileResponse,
  parentFolderId: string | null = null,
): DocumentsFileRow {
  return {
    kind: DOCUMENTS_TABLE_ROW_KIND.FILE,
    // Namespace folder-child rows so a file cannot share a React key across
    // two folders (or parent level) during a move.
    id: parentFolderId != null ? `${parentFolderId}:${file.id}` : file.id,
    parentFolderId,
    file,
  };
}

function composeDocumentsPlaceholderRow(
  parentFolderId: string,
  placeholder: DocumentsPlaceholderRow['placeholder'],
): DocumentsPlaceholderRow {
  return {
    kind: DOCUMENTS_TABLE_ROW_KIND.PLACEHOLDER,
    id: `${parentFolderId}-${placeholder}`,
    parentFolderId,
    placeholder,
  };
}

export function groupDocumentFilesByFolderId(
  files: DocumentFileResponse[] | undefined,
): Map<string, DocumentFileResponse[]> {
  const byFolderId = new Map<string, DocumentFileResponse[]>();
  files?.forEach((file) => {
    if (file.folderId == null) return;
    const folderFiles = byFolderId.get(file.folderId);
    if (folderFiles) folderFiles.push(file);
    else byFolderId.set(file.folderId, [file]);
  });
  return byFolderId;
}

export function getMatchingFolderFiles(
  files: DocumentFileResponse[] | undefined,
  searchTerm: string | undefined,
): DocumentFileResponse[] {
  return files?.filter((file) => lookFor(searchTerm, file, ['name'])) ?? [];
}

type ComposeDocumentsTableRowsWithExpansionParams = {
  topLevelRows: Array<DocumentsFolderRow | DocumentsFileRow>;
  expandedFolderIds: Set<string>;
  filesByFolderId: Map<string, DocumentFileResponse[]>;
  searchTerm?: string;
};

export function composeDocumentsTableRowsWithExpansion({
  topLevelRows,
  expandedFolderIds,
  filesByFolderId,
  searchTerm,
}: ComposeDocumentsTableRowsWithExpansionParams): DocumentsTableRow[] {
  const result: DocumentsTableRow[] = [];
  topLevelRows.forEach((row) => {
    result.push(row);
    if (
      row.kind !== DOCUMENTS_TABLE_ROW_KIND.FOLDER ||
      !expandedFolderIds.has(row.id)
    ) {
      return;
    }

    const files = filesByFolderId.get(row.id);
    const matchingFiles = getMatchingFolderFiles(files, searchTerm);
    if (matchingFiles.length) {
      matchingFiles.forEach((file) =>
        result.push(composeDocumentsFileRow(file, row.id)),
      );
      return;
    }

    result.push(
      composeDocumentsPlaceholderRow(
        row.id,
        files?.length
          ? EXPANDED_FOLDER_PLACEHOLDER.NO_MATCHES
          : EXPANDED_FOLDER_PLACEHOLDER.EMPTY,
      ),
    );
  });
  return result;
}

export function getFileByDocumentsRowIdMap(
  rows: DocumentsTableRow[],
): Map<string, DocumentFileResponse> {
  const map = new Map<string, DocumentFileResponse>();
  rows.forEach((row) => {
    if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE) map.set(row.id, row.file);
  });
  return map;
}

export function getDocumentsRowCreatedAt(row: DocumentsTableRow): string {
  if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER) {
    return format(new Date(row.folder.createdAt), 'P');
  }
  if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE) {
    return format(new Date(row.file.createdAt), 'P');
  }
  return '';
}

export function getDocumentsRowCreatedByName(row: DocumentsTableRow): string {
  if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER) {
    return row.folder.createdBy?.name ?? '';
  }
  if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE) {
    return row.file.createdBy?.name ?? '';
  }
  return '';
}
